import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import {
  loadBoardMinimizedViewConfig,
  MinimizedViewConfig,
  saveMinimizedViewConfigForBoard,
} from "../../../cores/boards/constants/minimized-view-fields";
import { useCrmBoardUi } from "./CrmBoardUiContext";
import MinimizeViewConfig from "./modal/MinimizeViewConfig";

interface Props {
  boardId: string;
  boardType?: string;
  listIds: string[];
  canAutomate: boolean;
  hasInactivityAutomations: boolean;
  onOpenInactivity: () => void;
}

export const CrmGlobalSettingsMenu = ({
  boardId,
  boardType,
  listIds,
  canAutomate,
  hasInactivityAutomations,
  onOpenInactivity,
}: Props) => {
  const intl = useIntl();
  const { requestGlobalMinimize, requestGlobalExpand } = useCrmBoardUi();
  const [minimizeOpen, setMinimizeOpen] = useState(false);
  const [initialConfig, setInitialConfig] = useState<MinimizedViewConfig>(() =>
    loadBoardMinimizedViewConfig(boardId)
  );

  const openMinimize = useCallback(() => {
    setInitialConfig(loadBoardMinimizedViewConfig(boardId));
    setMinimizeOpen(true);
  }, [boardId]);

  const onConfirmMinimize = useCallback(
    (config: MinimizedViewConfig) => {
      saveMinimizedViewConfigForBoard(boardId, listIds, config);
      requestGlobalMinimize(config);
      setMinimizeOpen(false);
    },
    [boardId, listIds, requestGlobalMinimize]
  );

  const label = useMemo(
    () =>
      intl.formatMessage({
        id: "app.crmBusiness.globalSettings",
      }),
    [intl]
  );

  return (
    <>
      <UncontrolledDropdown className="ml-1 ml-md-2">
        <DropdownToggle
          caret={false}
          className="whiteButton d-flex align-items-center"
          title={label}
          aria-label={label}
        >
          <FontAwesomeIcon icon={["fas", "cog"]} className="mr-md-1" />
          <span className="d-none d-md-inline">{label}</span>
        </DropdownToggle>
        <DropdownMenu right>
          <DropdownItem header>{label}</DropdownItem>
          <DropdownItem onClick={openMinimize}>
            <FontAwesomeIcon
              icon={["fas", "compress-alt"]}
              className="mr-2"
              fixedWidth
            />
            {intl.formatMessage({
              id: "app.crmBusiness.minimizeAllBoard",
            })}
          </DropdownItem>
          <DropdownItem onClick={requestGlobalExpand}>
            <FontAwesomeIcon
              icon={["fas", "expand-alt"]}
              className="mr-2"
              fixedWidth
            />
            {intl.formatMessage({
              id: "app.crmBusiness.expandAllBoard",
            })}
          </DropdownItem>
          {canAutomate && (
            <>
              <DropdownItem divider />
              <DropdownItem onClick={onOpenInactivity}>
                <FontAwesomeIcon
                  icon={["fas", "robot"]}
                  className={`mr-2 ${
                    hasInactivityAutomations ? "text-primary" : ""
                  }`}
                  fixedWidth
                />
                {intl.formatMessage({
                  id: "app.crmBusiness.notifyInactivity",
                })}
              </DropdownItem>
            </>
          )}
        </DropdownMenu>
      </UncontrolledDropdown>

      <MinimizeViewConfig
        isOpen={minimizeOpen}
        boardType={boardType}
        initialConfig={initialConfig}
        confirmLabelId="app.crmBusiness.minimizeAllBoard"
        onCancel={() => setMinimizeOpen(false)}
        onConfirm={onConfirmMinimize}
      />
    </>
  );
};
